import React, { useEffect, useState } from "react";
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  Cog6ToothIcon,
  CurrencyDollarIcon,
} from "@heroicons/react/24/outline";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  fetchMinimumMembershipFee,
  updateMinimumMembershipFee,
} from "../../features/admin/SettingSlice";
import { formatCurrencyVND } from "../../utils/helps";

const isSuperAdmin = () => String(localStorage.getItem("userId")) === "1";

const getUserPermissions = () => {
  try {
    const raw = localStorage.getItem("permissions");
    return raw ? JSON.parse(raw).map((p) => p.permissionSlug) : [];
  } catch {
    return [];
  }
};

const hasPermission = (slug) =>
  isSuperAdmin() || getUserPermissions().includes(slug);

const SystemSettings = () => {
  const dispatch = useDispatch();
  const { minimumMembershipFee, status, updateStatus } = useSelector(
    (state) => state.settings,
  );

  const [activeSetting, setActiveSetting] = useState(null);
  const [form, setForm] = useState({
    individualFee: 500000,
    fee: 2000000,
  });

  const canUpdate = hasPermission("SETTING_UPDATE");

  useEffect(() => {
    if (activeSetting === "minimum_membership_fee") {
      dispatch(fetchMinimumMembershipFee());
    }
  }, [dispatch, activeSetting]);

  useEffect(() => {
    if (minimumMembershipFee) {
      setForm({
        individualFee: minimumMembershipFee.individualFee || 500000,
        fee: minimumMembershipFee.fee || 2000000,
      });
    }
  }, [minimumMembershipFee]);

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleOpenMinimumMembershipFee = () => {
    setActiveSetting("minimum_membership_fee");
  };

  const handleBack = () => {
    setActiveSetting(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const individualFee = Number(form.individualFee);
    const fee = Number(form.fee);

    if (Number.isNaN(individualFee) || individualFee <= 0) {
      toast.error("Hội phí tối thiểu cá nhân không hợp lệ");
      return;
    }

    if (Number.isNaN(fee) || fee <= 0) {
      toast.error("Hội phí tối thiểu doanh nghiệp không hợp lệ");
      return;
    }

    try {
      await dispatch(
        updateMinimumMembershipFee({
          individualFee,
          fee,
        }),
      ).unwrap();

      toast.success("Cập nhật cài đặt thành công");
    } catch (error) {
      toast.error(error?.messages || error?.message || "Cập nhật thất bại");
    }
  };

  const handleReload = () => {
    dispatch(fetchMinimumMembershipFee());
  };

  if (activeSetting === "minimum_membership_fee") {
    return (
      <div className="p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-6">
          <div>
            <button
              type="button"
              className="btn btn-ghost btn-sm mb-3"
              onClick={handleBack}
            >
              <ArrowLeftIcon className="w-5 h-5" />
              Quay lại
            </button>

            <h1 className="text-2xl font-bold flex items-center gap-2">
              <CurrencyDollarIcon className="w-7 h-7 text-green-600" />
              Cài đặt hội phí tối thiểu
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Cấu hình mức hội phí tối thiểu cho hội viên cá nhân và hội viên
              doanh nghiệp
            </p>
          </div>

          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={handleReload}
            disabled={status === "loading"}
          >
            <ArrowPathIcon
              className={`w-5 h-5 ${
                status === "loading" ? "animate-spin" : ""
              }`}
            />
            Tải lại
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <div className="card bg-base-100 shadow">
              <div className="card-body">
                <div className="flex items-center gap-2 mb-2">
                  <CurrencyDollarIcon className="w-6 h-6 text-green-600" />
                  <h2 className="card-title text-lg">
                    Hội phí tối thiểu cá nhân
                  </h2>
                </div>

                <input
                  type="number"
                  min="1"
                  className="input input-bordered w-full"
                  value={form.individualFee}
                  onChange={(e) =>
                    handleChange("individualFee", e.target.value)
                  }
                  disabled={!canUpdate || status === "loading"}
                />

                <p className="text-sm text-gray-500">
                  Hiện tại: {formatCurrencyVND(Number(form.individualFee || 0))}
                </p>
              </div>
            </div>

            <div className="card bg-base-100 shadow">
              <div className="card-body">
                <div className="flex items-center gap-2 mb-2">
                  <CurrencyDollarIcon className="w-6 h-6 text-green-600" />
                  <h2 className="card-title text-lg">
                    Hội phí tối thiểu doanh nghiệp
                  </h2>
                </div>

                <input
                  type="number"
                  min="1"
                  className="input input-bordered w-full"
                  value={form.fee}
                  onChange={(e) => handleChange("fee", e.target.value)}
                  disabled={!canUpdate || status === "loading"}
                />

                <p className="text-sm text-gray-500">
                  Hiện tại: {formatCurrencyVND(Number(form.fee || 0))}
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            {canUpdate && (
              <button
                type="submit"
                className="btn btn-primary"
                disabled={updateStatus === "loading" || status === "loading"}
              >
                {updateStatus === "loading" ? "Đang lưu..." : "Lưu cài đặt"}
              </button>
            )}
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Cog6ToothIcon className="w-7 h-7 text-blue-600" />
          Cài đặt hệ thống
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Quản lý các cấu hình chung của hệ thống
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <button
          type="button"
          className="card bg-base-100 shadow hover:shadow-lg transition text-left"
          onClick={handleOpenMinimumMembershipFee}
        >
          <div className="card-body">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <CurrencyDollarIcon className="w-7 h-7 text-green-600" />
              </div>

              <div>
                <h2 className="card-title text-lg">Hội phí tối thiểu</h2>
                <p className="text-sm text-gray-500">
                  Cài đặt hội phí tối thiểu cho cá nhân và doanh nghiệp
                </p>
              </div>
            </div>
          </div>
        </button>
      </div>
    </div>
  );
};

export default SystemSettings;